// libraries
import { useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';

import { Divider, FormInstance, notification, Tooltip, Typography } from 'antd';
import { Form, Input, Select, DatePicker, Button } from 'antd';
import { LoadingOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';

import * as ActivityService from '../../../../services/bb-api/activity';
import { txt } from '../../../../locales/i18n';
import { SearchForm } from '../../../shared/search-form';
import { useFormFiller } from '../../../../hooks/form-filer';
import { queryMatchState } from '../../../../recoil/atoms';

const initialValues: any = {
	amount: 200,
	approved: 'not approved',
	status: 'active',
	date: [null, null],
	userId: '',
	activityId: '',
	filter: {
		target: 'all',
		search: '',
	},
};

export function ActivityListHeader({ setDataSource, useLoading }: any): JSX.Element {
	const [loading, setLoading] = useLoading;
	const setQueryMatch = useSetRecoilState(queryMatchState);

	const history = useHistory();

	const formRef = useRef<FormInstance>(null);
	const setQueryParams = useFormFiller(formRef);

	async function handleSubmit(values: any): Promise<void> {
		setLoading(true);
		setQueryParams(values);

		try {
			const { activities, totalMatches }: { activities: Record<string, any>[]; totalMatches: number } =
				await ActivityService.query(values);
			setDataSource(activities);
			setQueryMatch(totalMatches);
		} catch (error) {
			setDataSource([]);
			setQueryMatch(-1);

			const message: string = (error as Error).name;
			const description: string = (error as Error).message;
			notification.error({ message, description });
		}

		setLoading(false);
	}

	return (
		<SearchForm ref={formRef} initialValues={initialValues} onFinish={handleSubmit}>
			<Form.Item>
				<Button type="ghost" onClick={() => history.push('/activities/create')}>
					{txt('global.create')} <PlusOutlined />
				</Button>
			</Form.Item>

			<Divider type="vertical" />

			<Tooltip title={txt('global.approved')}>
				<Form.Item name="approved">
					<Select dropdownMatchSelectWidth={false}>
						<Select.Option value="all">{txt('global.all')}</Select.Option>
						<Select.Option value="approved">{txt('global.approved')}</Select.Option>
						<Select.Option value="not approved">{txt('global.not approved')}</Select.Option>
					</Select>
				</Form.Item>
			</Tooltip>

			<Tooltip title="Status">
				<Form.Item name="status">
					<Select placeholder="select status" dropdownMatchSelectWidth={false}>
						<Select.Option value="all">{txt('global.all')}</Select.Option>
						<Select.Option value="active">{txt('global.active')}</Select.Option>
						<Select.Option value="inactive">{txt('global.inactive')}</Select.Option>
					</Select>
				</Form.Item>
			</Tooltip>

			<Tooltip title={txt('global.filter')}>
				<Form.Item>
					<Input.Group compact>
						<Form.Item name={['filter', 'target']} noStyle>
							<Select dropdownMatchSelectWidth={false}>
								<Select.Option value="all">{txt('global.all')}</Select.Option>
								<Select.Option value="title">{txt('global.title')}</Select.Option>
								<Select.Option value="body">{txt('global.text')}</Select.Option>
								<Select.Option value="email">Email</Select.Option>
								<Select.Option value="municipality">{txt('global.municipality')}</Select.Option>
								<Select.Option value="universe">{txt('global.universe')}</Select.Option>
							</Select>
						</Form.Item>

						<Form.Item name={['filter', 'search']} noStyle>
							<Input style={{ width: 'fit-content' }} placeholder={txt('global.search')} allowClear />
						</Form.Item>
					</Input.Group>
				</Form.Item>
			</Tooltip>

			<Tooltip title={txt('global.user id')}>
				<Form.Item name="userId">
					<Input type="number" placeholder={txt('global.user id')} />
				</Form.Item>
			</Tooltip>

			<Tooltip title={txt('global.activity id')}>
				<Form.Item name="activityId">
					<Input type="number" placeholder={txt('global.activity id')} />
				</Form.Item>
			</Tooltip>

			<Tooltip title={txt('global.date')}>
				<Form.Item name="date">
					<DatePicker.RangePicker allowEmpty={[true, true]} />
				</Form.Item>
			</Tooltip>

			<Tooltip title={txt('global.amount')}>
				<Form.Item name="amount">
					<Select>
						<Select.Option value={50}>50</Select.Option>
						<Select.Option value={200}>200</Select.Option>
						<Select.Option value={500}>500</Select.Option>
						<Select.Option value={1000}>1000</Select.Option>
					</Select>
				</Form.Item>
			</Tooltip>

			<Form.Item>
				<Button type="primary" htmlType="submit">
					{txt('global.search')} <SearchOutlined />
				</Button>
			</Form.Item>
		</SearchForm>
	);
}
